import { diagnostic } from '../utils/diagnostics';
import { getNativeParentOAuthCallbackUrl } from '../utils/nativeParentOAuth';
// src/contexts/AuthContext.jsx
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';

import {
  auth,
  db,
  supabase
} from '../services/supabase';

import {
  beginParentOAuthTransaction,
  clearParentOAuthTransaction,
  getParentOAuthCallbackUrl,
} from '../utils/parentOAuth';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      'useAuth must be used within an AuthProvider'
    );
  }

  return context;
};

const PARENT_LOAD_TIMEOUT_MS = 12000;
const AUTH_INIT_TIMEOUT_MS = 15000;

/*
 * Session-storage marker used only to identify a legitimate
 * Supabase password-recovery flow.
 *
 * This prevents a normal authenticated parent session from
 * being treated as a password-reset session simply by visiting
 * /reset-password manually.
 */
export const PASSWORD_RECOVERY_SESSION_KEY =
  'mypiggy-parent-password-recovery';

export const AuthProvider = ({
  children
}) => {
  const [user, setUser] =
    useState(null);

  const [parent, setParent] =
    useState(null);

  const [
    initializing,
    setInitializing
  ] = useState(true);

  const [
    parentLoading,
    setParentLoading
  ] = useState(false);

  const [
    parentChecked,
    setParentChecked
  ] = useState(false);

  const [
    parentLoadError,
    setParentLoadError
  ] = useState(null);

  const mountedRef =
    useRef(true);

  const parentLoadAbortRef =
    useRef(null);

  const parentLoadSeqRef =
    useRef(0);

  const loadedUserIdRef =
    useRef(null);

  const loadingUserIdRef =
    useRef(null);

  const parentCheckedRef =
    useRef(false);

  const authEventSeenRef =
    useRef(false);

  /*
   * ---------------------------------------------------------
   * KEEP PARENT CHECKED REF CURRENT
   * ---------------------------------------------------------
   */
  useEffect(() => {
    parentCheckedRef.current =
      parentChecked;
  }, [parentChecked]);

  /*
   * ---------------------------------------------------------
   * LOAD PARENT PROFILE
   * ---------------------------------------------------------
   */
  const loadParentData = async (
    userId,
    {
      blockUI = false
    } = {}
  ) => {
    if (!userId) {
      return null;
    }

    if (
      loadingUserIdRef.current ===
      userId
    ) {
      return null;
    }

    loadingUserIdRef.current =
      userId;

    const mySeq =
      ++parentLoadSeqRef.current;

    parentLoadAbortRef.current
      ?.abort?.();

    const controller =
      new AbortController();

    parentLoadAbortRef.current =
      controller;

    let timedOut = false;

    const timeoutId =
      window.setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, PARENT_LOAD_TIMEOUT_MS);

    if (blockUI) {
      setInitializing(true);
    }

    setParentLoading(true);
    setParentChecked(false);
    setParentLoadError(null);

    try {
      const parentData =
        await db.getParent(
          userId,
          {
            signal:
              controller.signal
          }
        );

      if (
        mySeq !==
          parentLoadSeqRef.current ||
        !mountedRef.current
      ) {
        return null;
      }

      setParent(parentData);

      loadedUserIdRef.current =
        userId;

      return parentData;
    } catch (error) {
      if (
        mySeq !==
          parentLoadSeqRef.current ||
        !mountedRef.current
      ) {
        return null;
      }

      if (timedOut) {
        const timeoutError =
          new Error(
            'Timed out while loading your family profile.'
          );

        diagnostic('error', 'parent.profile.load_timeout');

        setParentLoadError(
          timeoutError
        );

        return null;
      }

      const message =
        String(
          error?.message || ''
        );

      if (
        error?.name ===
          'AbortError' ||
        message.includes(
          'AbortError'
        )
      ) {
        return null;
      }

      diagnostic('error', 'parent.profile.load_failed');

      setParentLoadError(error);

      return null;
    } finally {
      window.clearTimeout(
        timeoutId
      );

      if (
        loadingUserIdRef.current ===
        userId
      ) {
        loadingUserIdRef.current =
          null;
      }

      if (
        mySeq ===
          parentLoadSeqRef.current &&
        mountedRef.current
      ) {
        setParentLoading(false);
        setParentChecked(true);

        if (blockUI) {
          setInitializing(false);
        }
      }
    }
  };

  /*
   * ---------------------------------------------------------
   * AUTH INITIALIZATION
   * ---------------------------------------------------------
   */
  useEffect(() => {
    mountedRef.current = true;

    authEventSeenRef.current =
      false;

    const {
      data: {
        subscription
      }
    } =
      auth.onAuthStateChange(
        (
          event,
          session
        ) => {
          if (
            !mountedRef.current
          ) {
            return;
          }

          authEventSeenRef.current =
            true;

          /*
           * A password-recovery link creates an authenticated
           * Supabase recovery session.
           *
           * Record that event in sessionStorage so the reset
           * page can distinguish it from an ordinary logged-in
           * parent session.
           */
          if (
            event ===
            'PASSWORD_RECOVERY'
          ) {
            try {
              window.sessionStorage
                .setItem(
                  PASSWORD_RECOVERY_SESSION_KEY,
                  'true'
                );
            } catch (
              storageError
            ) {
              diagnostic('error', 'parent.recovery.storage_failed');
            }
          }

          const sessionUser =
            session?.user ??
            null;

          setUser(sessionUser);

          /*
           * -------------------------------------------------
           * NO SESSION / SIGNED OUT
           * -------------------------------------------------
           */
          if (!sessionUser) {
            parentLoadAbortRef
              .current
              ?.abort?.();

            loadingUserIdRef.current =
              null;

            loadedUserIdRef.current =
              null;

            setParent(null);

            setParentLoading(
              false
            );

            setParentChecked(
              true
            );

            setParentLoadError(
              null
            );

            setInitializing(
              false
            );

            return;
          }

          /*
           * -------------------------------------------------
           * TOKEN REFRESH / USER UPDATE
           * -------------------------------------------------
           */
          if (
            event ===
              'TOKEN_REFRESHED' ||
            event ===
              'USER_UPDATED'
          ) {
            if (
              loadedUserIdRef.current ===
                sessionUser.id &&
              parentCheckedRef.current
            ) {
              setInitializing(
                false
              );
            }

            return;
          }

          /*
           * PASSWORD_RECOVERY also represents a valid
           * authenticated user session, so allow the normal
           * parent profile load behavior to continue.
           */
          if (
            event !==
              'INITIAL_SESSION' &&
            event !==
              'SIGNED_IN' &&
            event !==
              'PASSWORD_RECOVERY'
          ) {
            return;
          }

          if (
            loadedUserIdRef.current ===
              sessionUser.id &&
            parentCheckedRef.current
          ) {
            setInitializing(
              false
            );

            return;
          }

          setParentChecked(false);
          setParentLoadError(null);

          window.setTimeout(
            () => {
              if (
                !mountedRef.current
              ) {
                return;
              }

              loadParentData(
                sessionUser.id,
                {
                  blockUI: true
                }
              );
            },
            0
          );
        }
      );

    /*
     * ---------------------------------------------------------
     * AUTH INITIALIZATION FAILSAFE
     * ---------------------------------------------------------
     */
    const authInitTimeout =
      window.setTimeout(
        () => {
          if (
            !mountedRef.current ||
            authEventSeenRef.current
          ) {
            return;
          }

          const timeoutError =
            new Error(
              'Authentication initialization timed out. Please reload the page.'
            );

          diagnostic('error', 'parent.auth.init_timeout');

          setParentLoadError(
            timeoutError
          );

          setParentChecked(
            true
          );

          setInitializing(
            false
          );
        },
        AUTH_INIT_TIMEOUT_MS
      );

    return () => {
      mountedRef.current =
        false;

      window.clearTimeout(
        authInitTimeout
      );

      subscription
        ?.unsubscribe?.();

      parentLoadAbortRef.current
        ?.abort?.();

      loadingUserIdRef.current =
        null;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * ---------------------------------------------------------
   * REFRESH PARENT PROFILE
   * ---------------------------------------------------------
   */
  const refreshParent = async ({
    blockUI = false
  } = {}) => {
    const userId = user?.id;

    if (!userId) {
      setParent(null);

      loadedUserIdRef.current =
        null;

      setParentChecked(true);
      setParentLoadError(null);

      return null;
    }

    return loadParentData(
      userId,
      {
        blockUI
      }
    );
  };

  /*
   * ---------------------------------------------------------
   * RESOLVE AUTHENTICATED PARENT STATE
   * ---------------------------------------------------------
   *
   * The database remains authoritative for deciding whether
   * this authenticated user is:
   *
   * - an existing parent
   * - a new parent
   * - an invited Guardian
   * - an identity conflict
   */
  const resolveParentAuthState =
    async () => {
      return db.resolveMyParentAuthState();
    };

  /*
   * ---------------------------------------------------------
   * SIGN UP
   * ---------------------------------------------------------
   */
  const signUp = async (
  email,
  password,
  name,
  {
    emailRedirectTo
  } = {}
) => {
  const redirectTo =
    emailRedirectTo ||
    `${window.location.origin}/parent/login`;

  const {
    data,
    error
  } =
    await auth.signUp(
      email,
      password,
      {
        data: {
          name
        },

        emailRedirectTo:
          redirectTo
      }
    );

  if (error) {
    throw error;
  }

  return {
    ...data,

    needsEmailConfirmation:
      !data.session
  };
};

  /*
   * ---------------------------------------------------------
   * SIGN IN
   * ---------------------------------------------------------
   */
  const signIn = async (
    email,
    password
  ) => {
    const {
      data,
      error
    } =
      await auth.signIn(
        email,
        password
      );

    if (error) {
      throw error;
    }

    return data;
  };

    /*
   * ---------------------------------------------------------
   * SIGN IN WITH GOOGLE
   * ---------------------------------------------------------
   */
  const signInWithGoogle = async ({
    flow = 'login',
    inviteToken = null,
  } = {}) => {
    const transaction =
      beginParentOAuthTransaction({
        flow,
        inviteToken,
      });

    try {
      const {
        data,
        error,
      } =
        await auth.signInWithGoogle({
          redirectTo:
            Capacitor.isNativePlatform()
              ? getNativeParentOAuthCallbackUrl()
              : getParentOAuthCallbackUrl(),
          skipBrowserRedirect:
            Capacitor.isNativePlatform(),
        });

      if (error) {
        throw error;
      }

      if (Capacitor.isNativePlatform()) {
        if (!data?.url) {
          throw new Error('Google sign-in did not provide an authorization URL.');
        }

        await Browser.open({ url: data.url });
      }

      return {
        ...data,
        transactionId:
          transaction.id,
      };
    } catch (googleError) {
      clearParentOAuthTransaction(
        transaction.id
      );

      throw googleError;
    }
  };

  /*
   * ---------------------------------------------------------
   * SIGN IN WITH APPLE (NATIVE IOS ONLY)
   * ---------------------------------------------------------
   */
  const signInWithApple = async () => {
    // signInWithIdToken establishes the normal parent Supabase session.
    // The existing SIGNED_IN listener loads parent/family data.
    return auth.signInWithApple();
  };

  /*
   * ---------------------------------------------------------
   * SIGN IN WITH APPLE (DEV WEB OAUTH)
   * ---------------------------------------------------------
   */
  const signInWithAppleWeb = async () => {
    if (Capacitor.isNativePlatform() ||
        import.meta.env.VITE_SUPABASE_URL !==
          'https://agyqzewkrfzmuvmhfiuy.supabase.co') {
      throw new Error('Apple web sign-in is not enabled for this environment.');
    }

    const transaction = beginParentOAuthTransaction({ flow: 'login' });

    try {
      const { data, error } = await auth.signInWithAppleWeb({
        redirectTo: getParentOAuthCallbackUrl(),
      });

      if (error) throw error;
      if (!data?.url) {
        throw new Error('Apple sign-in did not provide an authorization URL.');
      }

      return data;
    } catch (appleError) {
      clearParentOAuthTransaction(transaction.id);
      throw appleError;
    }
  };

  /*
   * ---------------------------------------------------------
   * SIGN OUT
   * ---------------------------------------------------------
   */
  const signOut = async () => {
    const {
      error
    } =
      await auth.signOut();

    if (error) {
      diagnostic('error', 'parent.auth.signout_failed');

      throw error;
    }

    /*
     * Remove any stale password-recovery marker when the
     * parent explicitly signs out.
     */
    try {
      window.sessionStorage
        .removeItem(
          PASSWORD_RECOVERY_SESSION_KEY
        );
    } catch (
      storageError
    ) {
      diagnostic('error', 'parent.recovery.cleanup_failed');
    }

    parentLoadAbortRef.current
      ?.abort?.();

    loadingUserIdRef.current =
      null;

    loadedUserIdRef.current =
      null;

    setUser(null);
    setParent(null);

    setParentLoading(false);
    setParentChecked(true);

    setParentLoadError(null);

    setInitializing(false);
  };

  /*
   * ---------------------------------------------------------
   * COMPLETE PARENT SETUP
   * ---------------------------------------------------------
   */
  const completeSetup =
    async () => {
      const {
        data: {
          user:
            currentUser
        },
        error:
          userError
      } =
        await auth.getUser();

      if (userError) {
        throw userError;
      }

      if (!currentUser) {
        throw new Error(
          'Not authenticated'
        );
      }

      const existing =
        await db.getParent(
          currentUser.id
        );

      if (existing) {
        setParent(
          existing
        );

        loadedUserIdRef.current =
          currentUser.id;

        setParentChecked(
          true
        );

        setParentLoadError(
          null
        );

        return existing;
      }

      const nameFromMeta =
        currentUser
          .user_metadata
          ?.name
          ?.trim() ||
        'Parent';

      const {
        data:
          signupResult,
        error:
          signupError
      } =
        await supabase.rpc(
          'complete_parent_signup',
          {
            p_parent_name:
              nameFromMeta,

            p_currency:
              'USD',

            p_require_income_approval:
              false
          }
        );

      if (signupError) {
        diagnostic('error', 'parent.signup.complete_failed');

        throw signupError;
      }

      if (
        !signupResult ||
        !Array.isArray(
          signupResult
        ) ||
        signupResult.length ===
          0
      ) {
        throw new Error(
          'Family setup completed, but no setup result was returned.'
        );
      }

      const parentData =
        await db.getParent(
          currentUser.id
        );

      if (!parentData) {
        throw new Error(
          'Family setup was created, but the parent profile could not be loaded.'
        );
      }

      setParent(
        parentData
      );

      loadedUserIdRef.current =
        currentUser.id;

      setParentChecked(
        true
      );

      setParentLoadError(
        null
      );

      return parentData;
    };

  return (
    <AuthContext.Provider
      value={{
        user,
        parent,

        initializing,
        parentLoading,
        parentChecked,
        parentLoadError,

        refreshParent,
        resolveParentAuthState,

        signUp,
        signIn,
        signInWithGoogle,
        signInWithApple,
        signInWithAppleWeb,
        signOut,

        completeSetup
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
